import { useEffect, useState, type ReactNode } from "react";
import { Alert, Box, Button, CircularProgress, Stack } from "@mui/material";
import auth from "../../../services/auth";
import {
  PermissionsProvider,
  loadPermissionsFromStorage,
  useStalePermissionsCacheGuard,
} from "../../../context/PermissionsContext";

type Props = { children: ReactNode };

function isAuthed(): boolean {
  return Boolean(auth.isLoggedIn && auth.getUserDetails());
}

function hashHasCode(hash: string): boolean {
  if (!hash) return false;
  try {
    return new URLSearchParams(hash.replace(/^#/, "")).has("code");
  } catch {
    return false;
  }
}

function TarsAuthGate({ children }: Props) {
  const [authed, setAuthed] = useState<boolean>(() => isAuthed());
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const cacheStale = useStalePermissionsCacheGuard();

  useEffect(() => {
    if (authed) return;
    let cancelled = false;
    (async () => {
      try {
        const details = await auth.authorize();
        if (cancelled) return;
        // A resolved-without-details result means auth.authorize() kicked off
        // the OpenID redirect and control is about to leave the tab. Keep the
        // spinner up — only a thrown error is an actual failure.
        if (details) setAuthed(true);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authed, attempt]);

  useEffect(() => {
    if (!authed) return;
    if (hashHasCode(window.location.hash)) {
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
    }
  }, [authed]);

  if (cacheStale) {
    return (
      <Box sx={{ maxWidth: 480, mx: "auto", mt: 8, p: 3 }}>
        <Alert severity="info">
          Signing you in again to refresh permissions…
        </Alert>
      </Box>
    );
  }

  if (failed) {
    return (
      <Box sx={{ maxWidth: 480, mx: "auto", mt: 8, p: 3 }}>
        <Stack spacing={2}>
          <Alert severity="error">
            Sign-in didn&apos;t complete. Please try again.
          </Alert>
          <Button
            variant="contained"
            onClick={() => {
              setFailed(false);
              setAttempt((n) => n + 1);
            }}
          >
            Retry sign-in
          </Button>
        </Stack>
      </Box>
    );
  }

  if (!authed) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PermissionsProvider permissions={loadPermissionsFromStorage()}>
      {children}
    </PermissionsProvider>
  );
}

export default TarsAuthGate;
